import React, { useState, useEffect, useCallback } from "react";
import { Table, Card, Tag, Button, Space, Input, DatePicker, message, Modal, Descriptions, Form, Select, InputNumber, Divider, Alert, Typography, Checkbox } from "antd";
import { SearchOutlined, ReloadOutlined, EyeOutlined } from "@ant-design/icons";
import moment from "moment";
import { getAllEnquiries, pickupEnquiry, createBookingFromEnquiry, fetchBlockedRoomNumbers } from "../../api/bookingEnquiries";
import lang from "../../helper/langHelper.js";
import api from "../../api/client";

const { RangePicker } = DatePicker;
const { Text } = Typography;

const EXTRA_RATE_CONFIG = {
  flight_assistance: 1500,
  extra_bedroom: 1200,
  extra_bathroom: 700,
  meal_breakfast: 350,
  meal_lunch: 550,
  meal_dinner: 600,
  airport_transfer: 900,
  spa_access: 1500,
  gym_access: 500,
  wifi_premium: 300,
  parking: 250,
  pickup_service: 900,
  drop_service: 900,
  service_fee_percent: 2,
  tax_percent: 12,
};

const FACILITY_DEFAULT_RATES = {
  wifi: 300,
  parking: 250,
  pool: 700,
  gym: 500,
  spa: 1500,
  restaurant: 600,
  "room service": 400,
  bar: 500,
  "conference room": 1200,
  breakfast: 350,
  lunch: 550,
  dinner: 600,
  "airport transfer": 900,
  "premium wifi": 300,
};

const BookingEnquiries = () => {
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [dateRange, setDateRange] = useState(null);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [bookingModalVisible, setBookingModalVisible] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [agentHotels, setAgentHotels] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [selectedServicesMap, setSelectedServicesMap] = useState({});
  const [customServiceName, setCustomServiceName] = useState("");
  const [customServicePrice, setCustomServicePrice] = useState(null);
  const [pickupAutoFilled, setPickupAutoFilled] = useState(false);
  const [dropAutoFilled, setDropAutoFilled] = useState(false);
  const [blockedRoomNumbers, setBlockedRoomNumbers] = useState([]);
  const [blockedRoomsLoading, setBlockedRoomsLoading] = useState(false);
  const [form] = Form.useForm();
  const selectedHotelId = Form.useWatch("hotel_id", form);
  const selectedRoomPlan = Form.useWatch("room_plan", form);
  const selectedRooms = Form.useWatch("number_of_rooms", form);
  const needPickupService = Form.useWatch("need_pickup_service", form);
  const needDropService = Form.useWatch("need_drop_service", form);
  const airlineServicePrice = Form.useWatch("airline_service_price", form);

  const profile = JSON.parse(localStorage.getItem("userProfile") || "{}");
  const agentId = profile?.agent_id;

  const fetchEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      if (!agentId) {
        setEnquiries([]);
        return;
      }
      const response = await getAllEnquiries(agentId);
      if (response.success) {
        const raw = response.data;
        setEnquiries(Array.isArray(raw) ? raw : []);
      } else {
        setEnquiries([]);
      }
    } catch (error) {
      console.error("Error:", error);
      setEnquiries([]);
    } finally {
      setLoading(false);
    }
  }, [agentId]);

  const loadAgentHotels = useCallback(async () => {
    if (!agentId) return;
    try {
      const res = await api.get(`/get_agent_hotels_details/${agentId}`);
      if (res?.data?.success) {
        setAgentHotels(res.data.data || []);
      }
    } catch (_err) {
      setAgentHotels([]);
    }
  }, [agentId]);

  useEffect(() => {
    fetchEnquiries();
    loadAgentHotels();
  }, [fetchEnquiries, loadAgentHotels]);

  const handlePickup = async (enquiry) => {
    try {
      setActionLoading(true);
      const res = await pickupEnquiry(enquiry.enquiry_id, agentId);
      if (res?.success) {
        message.success(res.message || "Enquiry picked");
        fetchEnquiries();
      } else {
        message.error(res?.message || "Unable to pickup enquiry");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Unable to pickup enquiry");
    } finally {
      setActionLoading(false);
    }
  };

  const openCreateBookingModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setPickupAutoFilled(false);
    setDropAutoFilled(false);
    form.resetFields();
    form.setFieldsValue({
      number_of_rooms: 1,
      need_pickup_service: false,
      need_drop_service: false,
      pickup_service_details: "",
      drop_service_details: "",
      airline_service_price: EXTRA_RATE_CONFIG.pickup_service,
    });
    setServiceOptions([]);
    setSelectedServicesMap({});
    setBlockedRoomNumbers([]);
    setBookingModalVisible(true);
  };

  useEffect(() => {
    if (!bookingModalVisible || !selectedHotelId || !selectedEnquiry?.checkIn || !selectedEnquiry?.checkOut) {
      if (!bookingModalVisible) setBlockedRoomNumbers([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setBlockedRoomsLoading(true);
      try {
        const checkIn = moment(selectedEnquiry.checkIn).toISOString();
        const checkOut = moment(selectedEnquiry.checkOut).toISOString();
        const data = await fetchBlockedRoomNumbers(selectedHotelId, checkIn, checkOut);
        if (cancelled) return;
        if (data?.success && Array.isArray(data.blocked_room_numbers)) {
          setBlockedRoomNumbers(
            data.blocked_room_numbers.map((x) => String(x).trim()).filter(Boolean)
          );
        } else {
          setBlockedRoomNumbers([]);
        }
      } catch (_err) {
        if (!cancelled) {
          setBlockedRoomNumbers([]);
          message.warning("Could not load room availability for these dates.");
        }
      } finally {
        if (!cancelled) setBlockedRoomsLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [bookingModalVisible, selectedHotelId, selectedEnquiry?.checkIn, selectedEnquiry?.checkOut]);

  const parseFacilityWithAmount = (raw) => {
    const str = String(raw || "").trim();
    const match = str.match(/^(.*?)[|:-]\s*(\d+(?:\.\d+)?)$/);
    if (!match) {
      const key = str.toLowerCase();
      return {
        label: str,
        amount: FACILITY_DEFAULT_RATES[key] || 400,
      };
    }
    return {
      label: match[1].trim(),
      amount: Number(match[2]) || 400,
    };
  };

  const handleCreateBooking = async (values) => {
    try {
      setActionLoading(true);
      const chosenPlan = roomPlanOptions.find((plan) => plan.value === values.room_plan);
      if (!chosenPlan) {
        message.error("Please select room plan");
        return;
      }
      const qty = Math.max(1, Number(values.number_of_rooms || 1));
      const floorNumber = Number(chosenPlan.floor ?? 0);
      const roomNumber = String(chosenPlan.roomNo || "").trim();
      if (!roomNumber) {
        message.error("Selected room is missing a room number");
        return;
      }
      const bookedRoom = [
        {
          floorNumber,
          rooms: Array.from({ length: qty }, () => ({
            roomNumber,
            available: false,
            price: chosenPlan.price,
            roomType: chosenPlan.type,
          })),
        },
      ];
      const payload = {
        agent_id: agentId,
        hotel_id: values.hotel_id,
        roomType: chosenPlan.type,
        number_of_rooms: values.number_of_rooms,
        room_fare: finalAmount,
        bookedRoom,
        invoice_note: values.invoice_note,
        need_pickup_service: !!values.need_pickup_service,
        need_drop_service: !!values.need_drop_service,
        pickup_service_details: values.pickup_service_details || "",
        drop_service_details: values.drop_service_details || "",
        airline_service_price: Number(values.airline_service_price || 0),
        selected_services: selectedServiceItems,
        invoice_breakdown: {
          base_rate: baseRate,
          nights: nightsCount,
          rooms: roomCount,
          stay_amount: stayAmount,
          mandatory_extra_amount: mandatoryExtraAmount,
          selected_extra_amount: selectedExtraServiceAmount,
          transport_service_amount: transportServiceAmount,
          service_fee: Math.round(serviceFee),
          tax_amount: Math.round(taxAmount),
          total: finalAmount,
        },
      };
      const res = await createBookingFromEnquiry(selectedEnquiry.enquiry_id, payload);
      if (res?.success) {
        message.success(res.message || "Booking created and invoice sent");
        setBookingModalVisible(false);
        fetchEnquiries();
      } else {
        message.error(res?.message || "Failed to create booking");
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to create booking");
    } finally {
      setActionLoading(false);
    }
  };

  const selectedHotel = agentHotels.find((hotel) => hotel.Hotel_Id === selectedHotelId);
  const roomPlanOptions = React.useMemo(() => {
    if (!selectedHotel?.floors?.length) return [];
    const blocked = new Set((blockedRoomNumbers || []).map((x) => String(x).trim()).filter(Boolean));
    const list = [];
    selectedHotel.floors.forEach((floor) => {
      (floor.rooms || []).forEach((room) => {
        if (!room?.type || !room?.price) return;
        if (room.status !== 1 || room.available !== true) return;
        const roomNo = String(room.room_number || room.roomNumber || "").trim();
        if (blocked.has(roomNo)) return;
        const planValue = `${room.type}__${Number(room.price)}__${roomNo}`;
        const floorNum = floor.floor_Number ?? floor.floor_number;
        list.push({
          value: planValue,
          type: room.type,
          price: Number(room.price),
          roomNo,
          floor: floorNum,
        });
      });
    });
    return list;
  }, [selectedHotel, blockedRoomNumbers]);

  useEffect(() => {
    if (!bookingModalVisible) return;
    const plan = form.getFieldValue("room_plan");
    if (!plan) return;
    const ok = roomPlanOptions.some((o) => o.value === plan);
    if (!ok) form.setFieldsValue({ room_plan: undefined });
  }, [bookingModalVisible, form, roomPlanOptions]);

  useEffect(() => {
    if (!bookingModalVisible || !selectedHotel || !selectedEnquiry) return;
    const options = [];
    const extras = selectedEnquiry?.extra_services || {};
    const meals = extras?.meals || {};
    const additional = extras?.additional_services || {};

    const pushOption = (key, label, amount, preselected = false) => {
      if (options.some((item) => item.key === key)) return;
      options.push({ key, label, amount, preselected });
    };

    if (selectedEnquiry?.need_flight) pushOption("flight_assistance", "Flight Assistance", EXTRA_RATE_CONFIG.flight_assistance, true);
    if (meals.breakfast) pushOption("meal_breakfast", "Breakfast", EXTRA_RATE_CONFIG.meal_breakfast, true);
    if (meals.lunch) pushOption("meal_lunch", "Lunch", EXTRA_RATE_CONFIG.meal_lunch, true);
    if (meals.dinner) pushOption("meal_dinner", "Dinner", EXTRA_RATE_CONFIG.meal_dinner, true);
    if (additional.airport_transfer) pushOption("airport_transfer", "Airport Transfer", EXTRA_RATE_CONFIG.airport_transfer, true);
    if (additional.spa_access) pushOption("spa_access", "Spa Access", EXTRA_RATE_CONFIG.spa_access, true);
    if (additional.gym_access) pushOption("gym_access", "Gym Access", EXTRA_RATE_CONFIG.gym_access, true);
    if (additional.wifi_premium) pushOption("wifi_premium", "Premium WiFi", EXTRA_RATE_CONFIG.wifi_premium, true);
    if (additional.parking) pushOption("parking", "Parking", EXTRA_RATE_CONFIG.parking, true);

    (selectedHotel.facilities || []).forEach((facility, index) => {
      const parsed = parseFacilityWithAmount(facility);
      const key = `hotel_facility_${index}_${parsed.label.toLowerCase().replace(/\s+/g, "_")}`;
      pushOption(key, parsed.label, parsed.amount, false);
    });

    setServiceOptions(options);
    const initial = {};
    options.forEach((opt) => {
      initial[opt.key] = {
        selected: opt.preselected,
        amount: opt.amount,
        label: opt.label,
      };
    });
    setSelectedServicesMap(initial);
  }, [bookingModalVisible, selectedHotelId, selectedEnquiry, selectedHotel]);

  const selectedServiceItems = Object.entries(selectedServicesMap)
    .filter(([, item]) => item.selected)
    .map(([service_key, item]) => ({
      service_key,
      label: item.label,
      amount: Number(item.amount || 0),
    }));

  const addCustomService = () => {
    const name = String(customServiceName || "").trim();
    const amount = Number(customServicePrice || 0);
    if (!name) {
      message.warning("Please enter service name");
      return;
    }
    if (!amount || amount < 0) {
      message.warning("Please enter valid service amount");
      return;
    }

    const key = `custom_${Date.now()}`;
    setServiceOptions((prev) => [
      ...prev,
      { key, label: name, amount, preselected: true },
    ]);
    setSelectedServicesMap((prev) => ({
      ...prev,
      [key]: {
        selected: true,
        amount,
        label: name,
      },
    }));
    setCustomServiceName("");
    setCustomServicePrice(null);
  };

  const nightsCount = selectedEnquiry?.checkIn && selectedEnquiry?.checkOut
    ? Math.max(1, moment(selectedEnquiry.checkOut).diff(moment(selectedEnquiry.checkIn), "days"))
    : 1;

  const selectedPlan = roomPlanOptions.find((r) => r.value === selectedRoomPlan);
  const baseRate = selectedPlan?.price || 0;
  const roomCount = Number(selectedRooms || 1);
  const stayAmount = baseRate * nightsCount * roomCount;
  const extraServices = selectedEnquiry?.extra_services || {};

  const mandatoryExtraAmount =
    (Math.max(0, (extraServices?.bedrooms || 1) - 1) * EXTRA_RATE_CONFIG.extra_bedroom) +
    (Math.max(0, (extraServices?.bathrooms || 1) - 1) * EXTRA_RATE_CONFIG.extra_bathroom);
  const selectedExtraServiceAmount = selectedServiceItems.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );
  const extraServiceAmount = mandatoryExtraAmount + selectedExtraServiceAmount;
  const transportServiceAmount =
    (needPickupService || needDropService) ? Number(airlineServicePrice || 0) : 0;

  const subTotal = stayAmount + extraServiceAmount + transportServiceAmount;
  const serviceFee = (subTotal * EXTRA_RATE_CONFIG.service_fee_percent) / 100;
  const taxAmount = ((subTotal + serviceFee) * EXTRA_RATE_CONFIG.tax_percent) / 100;
  const finalAmount = Math.round(subTotal + serviceFee + taxAmount);

  useEffect(() => {
    if (!bookingModalVisible) return;
    form.setFieldsValue({ room_fare: finalAmount });
  }, [finalAmount, bookingModalVisible, form]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "orange";
      case "confirmed":
        return "green";
      case "cancelled":
        return "red";
      case "completed":
        return "blue";
      default:
        return "default";
    }
  };

  const normalizedSearch = searchText.trim().toLowerCase();

  const filteredEnquiries = enquiries.filter((enquiry) => {
    let matchesSearch = true;
    let matchesDate = true;

    if (normalizedSearch) {
      const customerName = String(enquiry.customer_name || "").toLowerCase();
      const customerEmail = String(enquiry.customer_email || "").toLowerCase();
      const customerPhone = String(enquiry.customer_phone || "");
      const city = String(enquiry.city || "").toLowerCase();
      const enquiryId = String(enquiry.enquiry_id || "").toLowerCase();
      matchesSearch =
        customerName.includes(normalizedSearch) ||
        customerEmail.includes(normalizedSearch) ||
        customerPhone.includes(searchText.trim()) ||
        city.includes(normalizedSearch) ||
        enquiryId.includes(normalizedSearch);
    }

    if (dateRange && dateRange.length === 2) {
      const enquiryDate = moment(enquiry.createdAt);
      matchesDate = enquiryDate.isBetween(dateRange[0], dateRange[1], "day", "[]");
    }

    return matchesSearch && matchesDate;
  });

  const handleViewDetails = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setSelectedEnquiry(null);
  };

  const getServiceDetailsText = (service) => {
    if (!service?.need_service) return "";
    const details = service?.details || {};
    const terminal = (details.terminal || "").trim();
    const dropoffPoint = (details.dropoff_point || "").trim();
    const airportName = (details.airport_name || "").trim();
    if (terminal || dropoffPoint) {
      return [terminal || null, dropoffPoint || null, airportName || null].filter(Boolean).join(" | ");
    }
    const airlineNumber = details.airline_number || "-";
    const airlineName = details.airline_name || "-";
    return `${airlineNumber} | ${airlineName} | ${airportName || "-"}`;
  };

  const handleTransportServiceToggle = (type, checked) => {
    const isPickup = type === "pickup";
    const sourceService = isPickup
      ? selectedEnquiry?.pickup_service
      : selectedEnquiry?.dropoff_service;
    const fieldName = isPickup ? "pickup_service_details" : "drop_service_details";
    const setAutoFilled = isPickup ? setPickupAutoFilled : setDropAutoFilled;

    if (!checked) {
      setAutoFilled(false);
      form.setFieldsValue({ [fieldName]: "" });
      return;
    }

    if (sourceService?.need_service) {
      setAutoFilled(true);
      form.setFieldsValue({ [fieldName]: getServiceDetailsText(sourceService) });
      return;
    }

    setAutoFilled(false);
    form.setFieldsValue({ [fieldName]: "" });
  };

  const formatTransportService = (service) => {
    if (!service?.need_service) return "No";
    const details = service?.details || {};
    const terminal = (details.terminal || "").trim();
    const dropoffPoint = (details.dropoff_point || "").trim();
    const airportName = (details.airport_name || "").trim();
    if (terminal || dropoffPoint) {
      const parts = [
        terminal ? `Terminal: ${terminal}` : null,
        dropoffPoint ? `Drop-off: ${dropoffPoint}` : null,
        airportName ? `Airport: ${airportName}` : null,
      ].filter(Boolean);
      if (parts.length) return parts.join(" | ");
    }
    const airlineNumber = details.airline_number || "-";
    const airlineName = details.airline_name || "-";
    return `${airlineNumber} | ${airlineName} | ${airportName || "-"}`;
  };

  const formatExtraServices = (enquiry) => {
    const extra = enquiry?.extra_services;
    if (!extra?.need_extra_services) return "No extra services";

    const mealsList = [];
    if (extra?.meals?.breakfast) mealsList.push("Breakfast");
    if (extra?.meals?.lunch) mealsList.push("Lunch");
    if (extra?.meals?.dinner) mealsList.push("Dinner");

    const additionalList = [];
    if (extra?.additional_services?.airport_transfer) additionalList.push("Airport Transfer");
    if (extra?.additional_services?.spa_access) additionalList.push("Spa Access");
    if (extra?.additional_services?.gym_access) additionalList.push("Gym Access");
    if (extra?.additional_services?.wifi_premium) additionalList.push("Premium WiFi");
    if (extra?.additional_services?.parking) additionalList.push("Parking");

    return `Bedroom(s): ${extra?.bedrooms || 1}, Bathroom(s): ${extra?.bathrooms || 1}, Meals: ${mealsList.length ? mealsList.join(", ") : "None"}, Additional: ${additionalList.length ? additionalList.join(", ") : "None"}`;
  };

  const columns = [
    {
      title: "Enquiry ID",
      dataIndex: "enquiry_id",
      key: "enquiry_id",
      width: 120,
    },
    {
      title: "Customer Name",
      dataIndex: "customer_name",
      key: "customer_name",
      width: 150,
    },
    {
      title: "Check-in",
      dataIndex: "checkIn",
      key: "checkIn",
      width: 120,
      render: (date) => moment(date).format("YYYY-MM-DD"),
    },
    {
      title: "Check-out",
      dataIndex: "checkOut",
      key: "checkOut",
      width: 120,
      render: (date) => moment(date).format("YYYY-MM-DD"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.charAt(0).toUpperCase() + status?.slice(1)}
        </Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: 100,
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleViewDetails(record)}
          >
            View Details
          </Button>
          {record.status === "pending" && (
            <Button
              size="small"
              loading={actionLoading}
              onClick={() => handlePickup(record)}
            >
              Pickup
            </Button>
          )}
          {record.status === "picked_up" && record.picked_by_agent_id === agentId && (
            <Button
              size="small"
              type="dashed"
              onClick={() => openCreateBookingModal(record)}
            >
              Create Booking
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="site-card-border-less-wrapper">
      <Card title={lang("Booking Enquiries")}>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 8,
            justifyContent: "flex-end",
            marginBottom: 12,
          }}
        >
          <Input
            allowClear
            placeholder="Search by enquiry ID, customer, email, phone, city"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 320, maxWidth: "100%" }}
          />
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            placeholder={["Start Date", "End Date"]}
          />
          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSearchText("");
              setDateRange(null);
            }}
          >
            Reset
          </Button>
        </div>
        <Table
          columns={columns}
          dataSource={filteredEnquiries}
          rowKey="enquiry_id"
          // Enquiry model has numeric `children` (guest count); antd uses `children` for tree rows by default.
          childrenColumnName="__antd_nested_rows__"
          loading={loading}
          pagination={{
            total: filteredEnquiries.length,
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} enquiries`,
          }}
          scroll={{ x: 800 }}
        />
      </Card>

      <Modal
        title="Booking Enquiry Details"
        open={isModalVisible}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
        ]}
        width={800}
      >
        {selectedEnquiry && (
          <Card
            bodyStyle={{
              padding: 12,
              maxHeight: "65vh",
              overflowY: "auto",
            }}
          >
            <Descriptions
              column={2}
              bordered
              size="small"
              labelStyle={{ width: 130, whiteSpace: "normal" }}
              contentStyle={{ whiteSpace: "normal", wordBreak: "break-word" }}
            >
              <Descriptions.Item label="Enquiry ID" span={1}>
                {selectedEnquiry.enquiry_id}
              </Descriptions.Item>
              <Descriptions.Item label="Status" span={1}>
                <Tag color={getStatusColor(selectedEnquiry.status)}>
                  {selectedEnquiry.status?.charAt(0).toUpperCase() + selectedEnquiry.status?.slice(1)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Customer Name" span={1}>
                {selectedEnquiry.customer_name}
              </Descriptions.Item>
              <Descriptions.Item label="Email" span={1}>
                {selectedEnquiry.customer_email}
              </Descriptions.Item>
              <Descriptions.Item label="Phone" span={1}>
                {selectedEnquiry.customer_phone}
              </Descriptions.Item>
              <Descriptions.Item label="City" span={1}>
                {selectedEnquiry.city}
              </Descriptions.Item>
              <Descriptions.Item label="Check-in" span={1}>
                {moment(selectedEnquiry.checkIn).format("YYYY-MM-DD")}
              </Descriptions.Item>
              <Descriptions.Item label="Check-out" span={1}>
                {moment(selectedEnquiry.checkOut).format("YYYY-MM-DD")}
              </Descriptions.Item>
              <Descriptions.Item label="Total Days" span={1}>
                {selectedEnquiry.total_days}
              </Descriptions.Item>
              <Descriptions.Item label="Total Guests" span={1}>
                {selectedEnquiry.total_guests}
              </Descriptions.Item>
              <Descriptions.Item label="Adults" span={1}>
                {selectedEnquiry.adults}
              </Descriptions.Item>
              <Descriptions.Item label="Children" span={1}>
                {selectedEnquiry.children}
              </Descriptions.Item>
              <Descriptions.Item label="Need Flight" span={1}>
                <Tag color={selectedEnquiry.need_flight ? "green" : "default"}>
                  {selectedEnquiry.need_flight ? "Yes" : "No"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created Date" span={1}>
                {moment(selectedEnquiry.createdAt).format("YYYY-MM-DD HH:mm:ss")}
              </Descriptions.Item>
              <Descriptions.Item label="Pickup Service" span={2}>
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {formatTransportService(selectedEnquiry.pickup_service)}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Dropoff Service" span={2}>
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {formatTransportService(selectedEnquiry.dropoff_service)}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Extra Services" span={2}>
                <div style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                  {formatExtraServices(selectedEnquiry)}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="Special Request" span={2}>
                <div
                  style={{
                    maxHeight: 72,
                    overflowY: "auto",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    paddingRight: 6,
                  }}
                >
                  {selectedEnquiry.special_request || "No special request"}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        )}
      </Modal>

      <Modal
        title="Create Booking & Generate Invoice"
        open={bookingModalVisible}
        onCancel={() => setBookingModalVisible(false)}
        footer={null}
        width={860}
      >
        <Form form={form} layout="vertical" onFinish={handleCreateBooking}>
          <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: 16 }}>
            <div>
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 12 }}
                message={`Invoice for ${selectedEnquiry?.customer_name || "Customer"} (${selectedEnquiry?.enquiry_id || "-"})`}
                description={`Stay: ${moment(selectedEnquiry?.checkIn).format("DD MMM YYYY")} to ${moment(selectedEnquiry?.checkOut).format("DD MMM YYYY")} (${nightsCount} night${nightsCount > 1 ? "s" : ""})`}
              />
              <Form.Item
                label="Hotel"
                name="hotel_id"
                rules={[{ required: true, message: "Select hotel" }]}
              >
                <Select placeholder="Select hotel">
                  {agentHotels.map((hotel) => (
                    <Select.Option key={hotel.Hotel_Id} value={hotel.Hotel_Id}>
                      {hotel.Hotel_name} ({hotel.Hotel_Id})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                label="Room Type"
                name="room_plan"
                rules={[{ required: true, message: "Select room type" }]}
              >
                <Select
                  placeholder={blockedRoomsLoading ? "Loading availability…" : "Select room type"}
                  loading={blockedRoomsLoading}
                  notFoundContent={blockedRoomsLoading ? "Loading…" : "No rooms available for these dates"}
                >
                  {roomPlanOptions.map((room) => (
                    <Select.Option key={room.value} value={room.value}>
                      Room {room.roomNo || "-"} | {room.type} | $. {room.price}/night
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Showing {roomPlanOptions.length} available room(s). Rooms already confirmed or paid for overlapping
                dates are hidden.
              </Text>
              <Form.Item
                label="No. of Rooms"
                name="number_of_rooms"
                rules={[{ required: true, message: "Enter number of rooms" }]}
              >
                <InputNumber min={1} max={10} style={{ width: "100%" }} />
              </Form.Item>
              <Form.Item label="Invoice Note" name="invoice_note">
                <Input.TextArea rows={3} placeholder="Optional note for customer" />
              </Form.Item>
              <Card
                size="small"
                title="Airline Services"
                style={{ marginBottom: 12 }}
              >
                <Form.Item style={{ marginBottom: 12 }}>
                  <Space size={24}>
                    <Form.Item
                      name="need_pickup_service"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox
                        onChange={(e) =>
                          handleTransportServiceToggle("pickup", e.target.checked)
                        }
                      >
                        Pickup
                      </Checkbox>
                    </Form.Item>
                    <Form.Item
                      name="need_drop_service"
                      valuePropName="checked"
                      style={{ marginBottom: 0 }}
                    >
                      <Checkbox
                        onChange={(e) =>
                          handleTransportServiceToggle("drop", e.target.checked)
                        }
                      >
                        Drop
                      </Checkbox>
                    </Form.Item>
                  </Space>
                </Form.Item>

                {needPickupService && (
                  <>
                    <Form.Item
                      label="Pickup Details"
                      name="pickup_service_details"
                      style={{ marginBottom: 8 }}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value && value.trim() === "") {
                              return Promise.reject(new Error("Pickup details cannot be only spaces"));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={2}
                        readOnly={pickupAutoFilled}
                        placeholder={
                          pickupAutoFilled
                            ? "Pickup details auto-fetched from enquiry"
                            : "Enter pickup details manually (Airline No | Airline Name | Airport)"
                        }
                      />
                    </Form.Item>
                  </>
                )}

                {needDropService && (
                  <>
                    <Form.Item
                      label="Drop Details"
                      name="drop_service_details"
                      style={{ marginBottom: 8 }}
                      rules={[
                        {
                          validator: (_, value) => {
                            if (value && value.trim() === "") {
                              return Promise.reject(new Error("Drop details cannot be only spaces"));
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Input.TextArea
                        rows={2}
                        readOnly={dropAutoFilled}
                        placeholder={
                          dropAutoFilled
                            ? "Drop details auto-fetched from enquiry"
                            : "Enter drop details manually (Airline No | Airline Name | Airport)"
                        }
                      />
                    </Form.Item>
                  </>
                )}

                {(needPickupService || needDropService) && (
                  <Form.Item label="Airline Service Price" name="airline_service_price" style={{ marginTop: 8, marginBottom: 0 }}>
                    <InputNumber min={0} style={{ width: "100%" }} />
                  </Form.Item>
                )}
              </Card>
              <Card
                size="small"
                title="Extra Services (Auto from hotel + enquiry)"
                style={{ marginBottom: 12, maxHeight: 220, overflow: "auto" }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 120px 92px",
                    gap: 8,
                    marginBottom: 12,
                  }}
                >
                  <Input
                    placeholder="Custom service name"
                    value={customServiceName}
                    onChange={(e) => setCustomServiceName(e.target.value)}
                  />
                  <InputNumber
                    min={0}
                    placeholder="Price"
                    value={customServicePrice}
                    onChange={setCustomServicePrice}
                    style={{ width: "100%" }}
                  />
                  <Button type="primary" onClick={addCustomService}>
                    Add
                  </Button>
                </div>
                <Space direction="vertical" style={{ width: "100%" }} size={8}>
                  {serviceOptions.map((service) => (
                    <div
                      key={service.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 120px",
                        gap: 10,
                        alignItems: "center",
                      }}
                    >
                      <Checkbox
                        checked={!!selectedServicesMap[service.key]?.selected}
                        onChange={(e) => {
                          setSelectedServicesMap((prev) => ({
                            ...prev,
                            [service.key]: {
                              ...prev[service.key],
                              selected: e.target.checked,
                            },
                          }));
                        }}
                      >
                        {service.label}
                      </Checkbox>
                      <InputNumber
                        min={0}
                        value={selectedServicesMap[service.key]?.amount}
                        onChange={(val) => {
                          setSelectedServicesMap((prev) => ({
                            ...prev,
                            [service.key]: {
                              ...prev[service.key],
                              amount: Number(val || 0),
                            },
                          }));
                        }}
                        style={{ width: "100%" }}
                      />
                    </div>
                  ))}
                  {!serviceOptions.length && (
                    <Text type="secondary">No extra services configured for this enquiry/hotel.</Text>
                  )}
                </Space>
              </Card>
              <Form.Item name="room_fare" hidden>
                <InputNumber />
              </Form.Item>
            </div>

            <Card size="small" title="Auto Invoice Breakdown" bordered>
              <Space direction="vertical" style={{ width: "100%" }} size={6}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Base room rate / night</Text>
                  <Text>$. {baseRate || 0}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Nights x Rooms</Text>
                  <Text>{nightsCount} x {roomCount}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text strong>Stay amount</Text>
                  <Text strong>$. {stayAmount}</Text>
                </div>
                <Divider style={{ margin: "8px 0" }} />
                <div style={{ marginBottom: 4 }}>
                  <Text type="secondary">Extra services</Text>
                </div>
                {Math.max(0, (extraServices?.bedrooms || 1) - 1) > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Extra Bedroom x {Math.max(0, (extraServices?.bedrooms || 1) - 1)}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      $. {Math.max(0, (extraServices?.bedrooms || 1) - 1) * EXTRA_RATE_CONFIG.extra_bedroom}
                    </Text>
                  </div>
                )}
                {Math.max(0, (extraServices?.bathrooms || 1) - 1) > 0 && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Extra Bathroom x {Math.max(0, (extraServices?.bathrooms || 1) - 1)}
                    </Text>
                    <Text style={{ fontSize: 12 }}>
                      $. {Math.max(0, (extraServices?.bathrooms || 1) - 1) * EXTRA_RATE_CONFIG.extra_bathroom}
                    </Text>
                  </div>
                )}
                {selectedServiceItems.map((item) => (
                  <div
                    key={item.service_key}
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {item.label}
                    </Text>
                    <Text style={{ fontSize: 12 }}>$. {Number(item.amount || 0)}</Text>
                  </div>
                ))}
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: 2 }}>
                  <Text strong>Extra services total</Text>
                  <Text strong>$. {extraServiceAmount}</Text>
                </div>
                {(needPickupService || needDropService) && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <Text strong>Airline service total</Text>
                    <Text strong>$. {transportServiceAmount}</Text>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Service fee ({EXTRA_RATE_CONFIG.service_fee_percent}%)</Text>
                  <Text>$. {Math.round(serviceFee)}</Text>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text type="secondary">Tax ({EXTRA_RATE_CONFIG.tax_percent}%)</Text>
                  <Text>$. {Math.round(taxAmount)}</Text>
                </div>
                <Divider style={{ margin: "8px 0" }} />
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <Text strong style={{ fontSize: 16 }}>Final Invoice Amount</Text>
                  <Text strong style={{ fontSize: 18, color: "#eca413" }}>$. {finalAmount}</Text>
                </div>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Amount is auto-generated from hotel rates and enquiry requirements.
                </Text>
              </Space>
            </Card>
          </div>

          <Button type="primary" htmlType="submit" loading={actionLoading} block style={{ marginTop: 12 }}>
            Generate Professional Invoice & Submit Booking
          </Button>
        </Form>
      </Modal>
    </div>
  );
};

export default BookingEnquiries;
